import type { PractitionerPublicProfileDto } from "cmw-client";

export type Coordinates = {
  lat: number;
  lng: number;
};

export const GOOGLE_MAPS_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ?? "";

export const GOOGLE_MAPS_MAP_ID = process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID ?? "";

export const MAP_LIBRARIES = ["marker", "geometry"];

export const CHROMELESS_MAP_OPTIONS = {
  disableDefaultUI: true,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  clickableIcons: false,
} as const;

export const AUSTRALIA_CENTER: Coordinates = { lat: -25.2744, lng: 133.7751 };
export const AUSTRALIA_ZOOM = 4;
/* Frames ~10 suburbs; the narrower phone viewport needs one step further out */
export const LOCATION_ZOOM = 11;
export const MOBILE_LOCATION_ZOOM = 10;
export const HIGHLIGHT_ZOOM = 13;

export const MIN_LOADING_MS = 1000;
export const PAGE_SIZE = 20;
export const LOAD_MORE_MARGIN = "320px";
export const HOVER_DEBOUNCE_MS = 150;

/* Screen-px the selected pin sits below centre to clear the card; negative flips the shift */
const CARD_CLEARANCE_Y = 250;
export const MOBILE_CARD_CLEARANCE_Y = -160;
const FLY_TO_MS = 900;
const MAX_FLY_PX = 1600;

const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

const worldPointOf = (map: google.maps.Map, position: Coordinates) =>
  map.getProjection()?.fromLatLngToPoint(new google.maps.LatLng(position)) ?? null;

const screenDistance = (map: google.maps.Map, from: Coordinates, to: Coordinates, zoom: number) => {
  const a = worldPointOf(map, from);
  const b = worldPointOf(map, to);

  return a && b ? Math.hypot(a.x - b.x, a.y - b.y) * 2 ** zoom : null;
};

/* One pin per practitioner, so the first geocoded address is the one that gets placed. */
export const coordinatesOf = ({ practiceAddresses }: PractitionerPublicProfileDto): Coordinates | null => {
  for (const { lat, lng } of practiceAddresses) {
    if (lat !== undefined && lng !== undefined) return { lat, lng };
  }

  return null;
};

export const panToWithOffset = (map: google.maps.Map, position: Coordinates, offsetX: number) => {
  map.panTo(position);
  if (offsetX !== 0) map.panBy(-offsetX / 2, 0);
};

const withCardRoom = (map: google.maps.Map, position: Coordinates, zoom: number, clearanceY: number): Coordinates => {
  const projection = map.getProjection();
  const point = projection?.fromLatLngToPoint(new google.maps.LatLng(position));
  if (!projection || !point) return position;

  const shifted = new google.maps.Point(point.x, point.y - clearanceY / 2 ** zoom);

  return projection.fromPointToLatLng(shifted)?.toJSON() ?? position;
};

export const flyToWithCardRoom = (
  map: google.maps.Map,
  position: Coordinates,
  zoom: number,
  clearanceY: number = CARD_CLEARANCE_Y,
) => {
  const startCenter = map.getCenter();
  const startZoom = map.getZoom();
  const target = withCardRoom(map, position, zoom, clearanceY);

  if (!startCenter || startZoom === undefined) {
    map.setZoom(zoom);
    map.panTo(target);
    return () => {};
  }

  const from = startCenter.toJSON();
  const distance = screenDistance(map, from, target, startZoom);

  if (distance === null || distance > MAX_FLY_PX) {
    map.moveCamera({ center: target, zoom });
    return () => {};
  }

  let frame: number | null = null;
  let startedAt: number | null = null;

  const step = (now: number) => {
    startedAt ??= now;
    const progress = Math.min(1, (now - startedAt) / FLY_TO_MS);
    const eased = easeInOutCubic(progress);

    map.moveCamera({
      center: {
        lat: from.lat + (target.lat - from.lat) * eased,
        lng: from.lng + (target.lng - from.lng) * eased,
      },
      zoom: startZoom + (zoom - startZoom) * eased,
    });

    frame = progress < 1 ? requestAnimationFrame(step) : null;
  };

  frame = requestAnimationFrame(step);

  return () => {
    if (frame !== null) cancelAnimationFrame(frame);
  };
};
